/**
 * Hotels — one definition, used everywhere.
 *
 * ── HOTELS ARE A SECONDARY DIMENSION ───────────────────────────────────────────
 * The primary entity in GapGo is the RESTAURANT. A restaurant owns its menu, its
 * opening hours, its account, its payouts and its orders — all of those are keyed on
 * `restaurantId` and derived from the signed token, never from a hotel.
 *
 * A hotel is just *where a guest is sitting*: it comes off the QR code in the room,
 * it scopes prices and stock for that venue, and it labels an order. Nothing owns
 * anything because of a hotel.
 *
 * So this file is deliberately a small static list, not a service. There is no
 * hotels API and no `hotelapp-hotels` read, because building one now would give a
 * secondary dimension more machinery than the primary entity has.
 *
 * ── WHY IT EXISTS AT ALL ───────────────────────────────────────────────────────
 * The same four hotels were hardcoded in FIVE files, in five different shapes, and
 * the copies had already drifted: AdminPanel said "Pinnacle Holiday" while the other
 * four — including the guest-facing landing page — said "Pinnacle Holiday Lodge".
 * A guest and an admin were reading different names for the same venue.
 *
 * ── ROOM TO GROW ───────────────────────────────────────────────────────────────
 * When hotels do need to be data (a hotel signs and someone non-technical has to add
 * it), every screen already reads through `getHotels()`. Making that async and
 * backing it with a table is then a change to THIS FILE ONLY — no call site moves.
 * That is the whole point of routing everything through one accessor now.
 */

/**
 * `accent`, `shortName` and `rooms` are admin-dashboard presentation.
 * `tagline` is guest-facing copy on the in-room landing page.
 */
export const HOTELS = [
  {
    id: 'country-plaza',
    name: 'Country Plaza',
    shortName: 'CP',
    accent: '#F59E0B',
    rooms: 24,
    tagline: 'Where comfort meets elegance',
  },
  {
    id: 'pinnacle-holiday',
    // "Lodge" is part of the name. AdminPanel used to drop it.
    name: 'Pinnacle Holiday Lodge',
    shortName: 'PH',
    accent: '#38BDF8',
    rooms: 18,
    tagline: 'Your perfect retreat',
  },
  {
    id: 'mountain-view',
    name: 'Mountain View',
    shortName: 'MV',
    accent: '#4ADE80',
    rooms: 30,
    tagline: 'Breathtaking views, exceptional service',
  },
  {
    id: 'kookaburra',
    name: 'Kookaburra',
    shortName: 'KK',
    accent: '#A78BFA',
    rooms: 20,
    tagline: 'Australian hospitality at its finest',
  },
];

/**
 * The accessor every screen should use.
 *
 * Synchronous today because the list is static. Kept as a function rather than a
 * bare export so that making it async later does not touch a single call site.
 */
export function getHotels() {
  return HOTELS;
}

/** Everything known about one hotel, or undefined. */
export function getHotel(hotelId) {
  return HOTELS.find((h) => h.id === hotelId);
}

/**
 * Display name for a hotel id.
 *
 * Falls back to the id rather than rendering "undefined" — an unknown hotel should
 * look like a data problem, not a broken page.
 */
export function hotelName(hotelId) {
  const h = getHotel(hotelId);
  return (h && h.name) || hotelId || 'Hotel';
}

/** Lookup object, for call sites that want `byId[id]` rather than a find(). */
export function hotelsById() {
  return HOTELS.reduce((acc, h) => { acc[h.id] = h; return acc; }, {});
}

export default HOTELS;
